import { useState } from 'react';
import type { CompleteSprintBody, Sprint } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select, type SelectOption } from '../../components/Select';
import styles from './CompleteSprintDialog.module.css';

export type CompleteSprintDialogProps = {
  readonly open: boolean;
  readonly targetSprints: readonly Sprint[];
  readonly incompleteCount: number;
  readonly isPending: boolean;
  readonly error?: string | null;
  readonly onClose: () => void;
  readonly onConfirm: (body: CompleteSprintBody) => void;
};

type MoveTarget = 'backlog' | 'sprint' | 'new';

const MOVE_OPTIONS: readonly { readonly value: MoveTarget; readonly label: string }[] = [
  { value: 'backlog', label: 'В бэклог' },
  { value: 'sprint', label: 'В другой спринт' },
  { value: 'new', label: 'В новый спринт' },
];

/** UC-SPR-05: незавершённые задачи переносятся по явному выбору. */
export function CompleteSprintDialog({
  open,
  targetSprints,
  incompleteCount,
  isPending,
  error,
  onClose,
  onConfirm,
}: CompleteSprintDialogProps) {
  const [moveTo, setMoveTo] = useState<MoveTarget>('backlog');
  const [targetSprintId, setTargetSprintId] = useState<string | null>(null);
  const [newSprintName, setNewSprintName] = useState('');

  const sprintOptions: readonly SelectOption<string>[] = targetSprints.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const isInvalid =
    (moveTo === 'sprint' && targetSprintId === null) ||
    (moveTo === 'new' && newSprintName.trim().length === 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Завершение спринта"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={isInvalid}
            onClick={() =>
              onConfirm({
                moveTo,
                targetSprintId: moveTo === 'sprint' ? targetSprintId : null,
                newSprintName: moveTo === 'new' ? newSprintName.trim() : null,
              })
            }
          >
            Завершить спринт
          </Button>
        </>
      }
    >
      <div className={styles['body']}>
        <p className={styles['text']}>
          {incompleteCount === 0
            ? 'Все задачи спринта выполнены — переносить нечего.'
            : `${incompleteCount} незавершённых задач нужно перенести.`}
        </p>

        {incompleteCount > 0 ? (
          <>
            <div className={styles['options']} role="radiogroup" aria-label="Куда перенести незавершённые задачи">
              {MOVE_OPTIONS.map((option) => (
                <label key={option.value} className={styles['option']}>
                  <input
                    type="radio"
                    name="moveTo"
                    value={option.value}
                    checked={moveTo === option.value}
                    onChange={() => setMoveTo(option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>

            {moveTo === 'sprint' ? (
              <Select
                label="Целевой спринт"
                options={sprintOptions}
                value={targetSprintId}
                placeholder="Выберите спринт"
                onChange={setTargetSprintId}
              />
            ) : null}

            {moveTo === 'new' ? (
              <Input
                label="Название нового спринта"
                value={newSprintName}
                onChange={(event) => setNewSprintName(event.target.value)}
              />
            ) : null}
          </>
        ) : null}

        {error ? (
          <p className={styles['error']} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
